import { FC, useEffect, useState } from 'react';

import { toYmd } from '../../utils/rebate-settlement.utils';
import { ceilWon, formatWon } from '../../utils/settlement.utils';

// ─────────────────────────────────────────────
// PaidConfirmModal
//
// 정산 완료 내역에서 Draft 정산을 지급완료(Paid)로 확정하는 모달.
// 실제 지급액·지급일을 입력해 동결한다. 업체정산·상조정산 두 섹션이 공유한다.
// ─────────────────────────────────────────────

export interface PaidConfirmModalProps {
  open: boolean;
  targetName: string;
  count: number;
  amount: number;
  onClose: () => void;
  /** executedAt: 선택한 지급일(ISO 문자열), paidAmount: 실제 지급액(계산값과 다르면 동결) */
  onConfirm: (executedAt: string, paidAmount: number) => Promise<void>;
}

export const PaidConfirmModal: FC<PaidConfirmModalProps> = ({
  open,
  targetName,
  count,
  amount,
  onClose,
  onConfirm,
}) => {
  // 계산식 기준 금액 — 실제 지급액 입력의 기본값/참조값
  const computed = ceilWon(amount);
  const [date, setDate] = useState(() => toYmd(new Date()));
  const [paid, setPaid] = useState<number>(computed);
  const [loading, setLoading] = useState(false);

  // 모달은 항상 트리에 남아있어 재오픈 시 값이 초기화되지 않으므로, 열릴 때 오늘/계산금액으로 리셋한다.
  useEffect(() => {
    if (open) {
      setDate(toYmd(new Date()));
      setPaid(ceilWon(amount));
    }
  }, [open, amount]);

  if (!open) return null;

  const diff = paid - computed;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      // 선택한 날짜를 로컬 정오로 고정해 타임존에 따른 날짜 밀림을 방지한다.
      await onConfirm(new Date(`${date}T12:00:00`).toISOString(), paid);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-base font-bold text-gray-800">지급완료 확정</h3>
        <div className="mb-4 space-y-2 text-sm text-gray-600">
          <div>
            <span className="font-medium">대상:</span> {targetName}
          </div>
          <div>
            <span className="font-medium">정산 건수:</span> {count}건
          </div>
          <div>
            <span className="font-medium">계산 금액:</span> {formatWon(computed)}
          </div>
        </div>
        <div className="mb-2 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">실제 지급액</span>
          <input
            type="number"
            value={paid}
            min={0}
            step={1}
            onChange={(e) => setPaid(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            className="w-36 rounded border border-gray-300 px-2 py-1 text-right tabular-nums"
          />
          <span>원</span>
        </div>
        {diff !== 0 && (
          <p className="mb-3 text-xs text-amber-600">
            계산 금액과 {formatWon(Math.abs(diff))} {diff > 0 ? '많음' : '적음'} — 입력한 금액으로 확정됩니다.
          </p>
        )}
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">지급일</span>
          <input
            type="date"
            value={date}
            max={toYmd(new Date())}
            onChange={(e) => setDate(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1"
          />
        </div>
        <p className="mb-5 text-xs text-gray-400">
          실제 지급(이체)한 금액과 날짜를 입력하세요. 확정 후에는 Draft로 되돌리기 전까지 수정할 수 없습니다.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? '처리 중...' : '지급완료 확정'}
          </button>
        </div>
      </div>
    </div>
  );
};
